import { prop } from 'ramda';
import type { ResponseEither } from '@/models/Common/ResponseEither';
import type { IRequesterError, IRequesterResponse } from '@/services/RequestService/IRequester';

export interface IReactQueryResponseHandlerCallback<P, R> {
  responseCallback?: (parameter: IRequesterResponse<P>, resolve: (value?: R | PromiseLike<R>) => void) => void;
  errorCallback?: (
    error: IRequesterError,
    resolve: (value?: R | PromiseLike<R>) => void,
    reject: (reason?: any) => void,
  ) => void;
}

/**
 * @Summary     기존 BaseAction(RequestBuilder)에서 응답받는 데이터는 ResponseEither 타입이다.
 *              React Query 는 넘겨진 promise 를 반환하는 콜백의 resolve, reject 여부를 가지고 응답 성공, 실패를 판단하므로,
 *              ResponseEither 타입을 caseOf 따위로 처리한다면 React Query 에서 응답 성공/실패를 판단하지 못한다.
 *              위와 같은 상황을 방지하기 위해 ResponseEither caseOf left, right 에 대한 분기처리를 제공하는 함수.
 * @Description 첫 번째 인자로 넘겨준 promise 를 Promise 객체로 감싸 실행한다. caseOf left 일 때, reject 를 호출하고,
 *              caseOf right 일 때, resolve 를 호출하여 React Query 에게 응답/실패를 알린다.
 *
 * @template    P                 promise(첫번째 인자)의 caseOf right 의 type.
 * @template    R                 responseCallback 에서 resolve 에 넘겨줄 data type. default 로 P 값을 가진다.
 * @param       promise           Promise<ResponseEither> 객체. 기존에 쓰던 new BaseAction(RequestBuilder).{method} 따위를 넘겨주면 된다.
 * @param       errorCallback     caseOf left 일 때, 응답 받은 에러 데이터에 대한 처리를 추가적으로 수행하는 callBack. 에러 데이터와 resolve, reject 함수를 인자로 받는다.
 *                                default 데이터 노출이 필요한 경우 인자의 resolve 함수를 실행한다.
 * @param       responseCallback  caseOf right 일 때, 응답 받은 데이터에 대한 처리를 추가적으로 수행하는 callBack. 데이터와 resolve 함수를 인자로 받는다.
 */
const reactQueryResponseHandler = <P, R = P>(
  promise: Promise<ResponseEither<P>>,
  { errorCallback, responseCallback }: IReactQueryResponseHandlerCallback<P, R> = {},
): Promise<R> => {
  return new Promise(async (resolve, reject) => {
    (await promise).caseOf({
      left: (e) => {
        if (errorCallback) {
          errorCallback(e, resolve, reject);
        } else {
          reject(e);
        }
      },
      right: (payload) => {
        if (responseCallback) {
          responseCallback(payload, resolve);
        } else {
          resolve((prop('data', payload) as unknown) as R);
        }
      },
    });
  });
};

export default reactQueryResponseHandler;
